<template>
  <v-col class="px-0" cols="12" ref="kwContainer">
    <div
      v-for="(keyword, index) in keywords"
      :key="index"
      class="keyword-row d-flex flex-column flex-md-row ga-3 align-md-start pb-5"
    >
      <div class="keyword-lang-col pr-md-5">
        <div :class="selectedNative">
          <div class="d-inline-flex align-center justify-start">
            <span class="h3 keyword">{{ translateKeyword(keyword, selectedNative)?.title }}</span>
            <PlayNarrationButton
              v-if="getKeywordAudioData(translateKeyword(keyword, selectedNative)) && !printView"
              class="ml-2"
              :value="getKeywordAudioData(translateKeyword(keyword, selectedNative))"
            />
          </div>
          <p v-if="isExplanationVisible">
            {{ translateKeyword(keyword, selectedNative)?.description }}
          </p>
        </div>
      </div>
      <div v-if="selectedTranslation" class="keyword-lang-col pr-md-5">
        <v-row
          class="flex-nowrap ma-0"
          :justify="
            translateKeyword(keyword, selectedTranslation) && !isRTL(selectedTranslation) ? 'space-between' : 'end'
          "
          :align="isExplanationVisible ? 'start' : 'center'"
        >
          <div
            v-if="translateKeyword(keyword, selectedTranslation)?.title"
            :dir="isRTL(selectedTranslation) ? 'rtl' : 'ltr'"
          >
            <div class="d-flex align-center justify-start">
              <span class="h3 keyword">{{ translateKeyword(keyword, selectedTranslation).title }}</span>
              <PlayNarrationButton
                v-if="
                  getKeywordAudioData(translateKeyword(keyword, selectedTranslation)) &&
                  !printView &&
                  selectedTranslation === 'sv'
                "
                class="ml-2"
                :value="getKeywordAudioData(translateKeyword(keyword, selectedTranslation))"
              />
            </div>
            <p v-if="isExplanationVisible">{{ translateKeyword(keyword, selectedTranslation)?.description }}</p>
          </div>
        </v-row>
      </div>
      <div
        v-if="mdAndUp"
        class="keyword-image-col align-self-stretch d-flex align-center"
        :style="{ width: imageColWidth }"
      >
        <template v-if="!printView">
          <button
            v-if="keyword.imageId"
            @click="openOverlay(getKeywordImageUrl(keyword, 800))"
            :aria-label="t(l.film.button_keyword_image)"
            class="w-75 h-75 ml-4 pointer"
            style="max-width: 116px"
          >
            <v-img
              :src="getKeywordImageUrl(keyword, 116, 116)"
              cover
              :alt="altTexts ? altTexts[keyword.imageId] : ''"
              max-height="116"
              min-width="74"
            />
          </button>
        </template>
        <template v-else>
          <img
            :src="getKeywordImageUrl(keyword, 116, 116)"
            alt=""
            :height="selectedTranslation ? 50 : 100"
            class="ml-4 pointer"
            style="background-size: cover"
          />
        </template>
      </div>
    </div>
    <ImageModal v-model="showKeywordImageModal" :imageUrl="overlayImageUrl" />
  </v-col>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import useKeywordsStore, { KeywordWithTranslations } from '@/store/keywords';
import isRTL from '@/composables/use-is-rtl-language';
import { storeToRefs } from 'pinia';
import useAudioStore from '@/store/audio';
import ImageModal from '../../modal/ImageModal.vue';
import PlayNarrationButton from '../../base/PlayNarrationButton.vue';
import { useDialogFocus } from '@ilteducation/components';
import { useLocale } from '@/plugins/translation';
import { useDisplay } from 'vuetify';
import useEqualHeights from '@/composables/use-equal-row-heights';

type Props = {
  selectedNative: string;
  selectedTranslation?: string;
  keywords: KeywordWithTranslations[];
  altTexts: Record<string, string>;
  isExplanationVisible: boolean;
  printView?: boolean;
  asideWidth?: number;
};
const props = defineProps<Props>();

// Reserve exactly the same width for the image slot as the dropdown row reserves for
// its buttons, so the two language columns flex-grow to the same width as the dropdowns.
const FALLBACK_ASIDE_WIDTH = 116;
const imageColWidth = computed(() => `${props.asideWidth || FALLBACK_ASIDE_WIDTH}px`);

const { t, l } = useLocale();

const { getKeywordImageUrl, translateKeyword } = useKeywordsStore();
const { audioResources } = storeToRefs(useAudioStore());
const showKeywordImageModal = ref(false);
const overlayImageUrl = ref('');
const { rememberFocus } = useDialogFocus([showKeywordImageModal]);
const { mdAndUp } = useDisplay();

const openOverlay = (imageUrl: string) => {
  rememberFocus();
  overlayImageUrl.value = imageUrl;
  showKeywordImageModal.value = true;
};
const getKeywordAudioData = computed(() => (kw?: { title: string; description: string }) => {
  if (!kw) {
    return null;
  }
  const title = audioResources.value.find((item: { key: string }) => item.key === kw.title);
  const description = audioResources.value.find((item: { key: string }) => item.key === kw.description);
  if (props.isExplanationVisible) {
    if (description && title && title.id && description.id) {
      return [title, description];
    }
    return null;
  }
  return title ? [title] : null;
});

const kwContainer = ref();
const { equalize } = useEqualHeights(kwContainer, '.keyword-row');

watch(
  () => [props.selectedTranslation, props.isExplanationVisible],
  async () => {
    await nextTick();
    equalize();
  },
  { immediate: true },
);
</script>
<style lang="scss" scoped>
.keyword {
  font-size: 1rem;

  &-item__right {
    .v-img {
      margin-top: 0.5rem; // align with keyword heading
    }
  }
}
.keyword-row:first-child {
  border-top: 1px solid rgb(var(--v-theme-gray-lighten-4));
}

.keyword-row {
  border-bottom: 1px solid rgb(var(--v-theme-gray-lighten-4));
  break-inside: avoid;

  .ar,
  .prs,
  .fa {
    direction: rtl !important;
  }
}

.keyword-lang-col {
  min-width: 0; // allow long words/sentences to wrap instead of stretching the column
}

.keyword-image-col {
  flex: 0 0 auto;
}

// From the md breakpoint up, mirror the dropdown row: the two language columns
// flex-grow equally while the image slot keeps its fixed (button-matched) width.
@media (min-width: 960px) {
  .keyword-lang-col {
    flex: 1 1 0;
    padding-top: 0.75rem; // nudge text down so it reads as vertically centered, like the thumbnail
  }
}
</style>
